/**
 * utils/txnStock.js
 * Pure stock-delta application for transactions (Phase T1 PRD §4.2). One function drives
 * create (+1), delete (-1), and edit (reverse old with -1, then apply new with +1).
 *
 * PENJUALAN subtracts from the anchor bucket. PEMBELIAN adds to it. TRANSFER subtracts the
 * source bucket AND adds the destination bucket atomically (both effects land in one update).
 * `txn.location` is the anchor for PENJUALAN/PEMBELIAN; for TRANSFER it is the SOURCE bucket
 * (the other bucket is the destination — only two buckets exist, so no separate lookup is
 * needed). Negative stock is legal everywhere (existing invariant #2) — never clamp, never
 * block. SETs are ordinary items: this function only ever touches stokPusat/stokCabang.
 *
 * A line's `qty` is coerced via `Number(...)` and the line is silently skipped (defense in
 * depth, not currently reachable — the form already blocks a bad qty from being submitted) if
 * the result isn't finite, mirroring the coerce-then-skip pattern `transformations.js`'s own
 * `aggregateLines` already uses for the same class of bad input.
 */

const bucketField = (location) => (location === "PUSAT" ? "stokPusat" : "stokCabang");
const otherBucketField = (location) => (location === "PUSAT" ? "stokCabang" : "stokPusat");

/**
 * Apply (sign=+1) or reverse (sign=-1) a transaction's stock effect. Returns new data;
 * never mutates. Lines referencing an internalCode absent from data.items are silently
 * skipped (no item to update against).
 */
export function applyTxnStockDelta(data, txn, sign) {
  const lines = txn.lines || [];
  if (lines.length === 0) return data;

  // Merge per (internalCode, field) so a transaction with a repeated code on the same field
  // (or a TRANSFER's source+dest hitting the SAME field across different lines) applies as
  // one atomic delta per item, not a series of intermediate writes.
  const deltas = new Map(); // internalCode -> { stokPusat: number, stokCabang: number }
  const bump = (internalCode, field, amount) => {
    const cur = deltas.get(internalCode) || { stokPusat: 0, stokCabang: 0 };
    cur[field] += amount;
    deltas.set(internalCode, cur);
  };

  if (txn.type === "PENJUALAN") {
    const field = bucketField(txn.location);
    for (const line of lines) {
      const qty = Number(line.qty);
      if (!Number.isFinite(qty)) continue;
      bump(line.internalCode, field, -qty * sign);
    }
  } else if (txn.type === "PEMBELIAN") {
    const field = bucketField(txn.location);
    for (const line of lines) {
      const qty = Number(line.qty);
      if (!Number.isFinite(qty)) continue;
      bump(line.internalCode, field, qty * sign);
    }
  } else if (txn.type === "TRANSFER") {
    const sourceField = bucketField(txn.location);
    const destField = otherBucketField(txn.location);
    for (const line of lines) {
      const qty = Number(line.qty);
      if (!Number.isFinite(qty)) continue;
      bump(line.internalCode, sourceField, -qty * sign);
      bump(line.internalCode, destField, qty * sign);
    }
  } else {
    return data;
  }

  if (deltas.size === 0) return data;

  const items = data.items.map((item) => {
    const d = deltas.get(item.internalCode);
    if (!d || (d.stokPusat === 0 && d.stokCabang === 0)) return item;
    return {
      ...item,
      stokPusat: item.stokPusat + d.stokPusat,
      stokCabang: item.stokCabang + d.stokCabang,
    };
  });

  return { ...data, items };
}
